/**
 * 統一認證服務
 *
 * 核心職責：session 生命週期 + auth listener
 * 子服務（delegation）：
 * - sessionCache.ts — session 快取 + token 刷新
 * - oauthProviders.ts — OAuth / Email 登入方法
 * - user/userProfileService.ts — users 表 CRUD
 * - user/userPreferencesService.ts — 組織選擇 + 過濾器偏好
 */

import { supabase } from '@shared/services/core/supabaseClient'
import type { Session, User as SupabaseUser, Subscription } from '@supabase/supabase-js'
import { sessionCacheService } from './sessionCache'
import { signInWithGoogle as _signInWithGoogle, signInWithLine as _signInWithLine, signInWithFacebook as _signInWithFacebook, signInWithProvider as _signInWithProvider, signInWithRedirect as _signInWithRedirect, signUp as _signUp, signInWithPassword as _signInWithPassword } from './oauthProviders'
import { userProfileService } from '../user/userProfileService'
import { saveLastSelectedOrganization as _saveLastSelectedOrganization, getLastSelectedOrganization as _getLastSelectedOrganization, saveFilterPreferences as _saveFilterPreferences, getFilterPreferences as _getFilterPreferences } from '../user/userPreferencesService'
import { convertSupabaseUserToAuthUser, type AuthEvent, type AuthUser, type AuthStateCallback, type AuthStats, type OAuthError, type ProviderName, type UserProfile, type UserRow } from './types'

// 重新導出所有型別，保持向後相容（41 個外部呼叫者不需要修改）
export type { AuthEvent, AuthUser, FirebaseCompatibleUser, UserProfile, UserRow, AuthStateCallback, AuthStats, OAuthError, ProviderName } from './types'

// ============ 常數定義 ============

// convertUserRowToProfile, convertProfileToUserRow → user/userProfileService.ts
// cleanProviderName → oauthProviders.ts

// ============ 服務類別 ============

class AuthService {
  private readonly SESSION_TIMEOUT = 10000

  // Auth 就緒狀態
  private authReadyPromise: Promise<AuthUser | null>
  private authReadyResolve!: (user: AuthUser | null) => void
  public isAuthReady = false
  public currentUser: AuthUser | null = null

  // 統一的認證狀態監聽器
  private _authStateCallbacks: Set<AuthStateCallback> = new Set()
  private _authSubscription: Subscription | null = null
  private _isInitialized = false
  private _initializingPromise: Promise<AuthUser | null> | null = null


  constructor() {
    this.authReadyPromise = this._initAuthReadyPromise()
  }

  /**
   * 初始化 auth 就緒 Promise
   */
  private _initAuthReadyPromise(): Promise<AuthUser | null> {
    return new Promise<AuthUser | null>(resolve => {
      this.authReadyResolve = resolve
    })
  }

  /**
   * 等待認證初始化完成
   */
  ensureAuthReady(): Promise<AuthUser | null> {
    return this.authReadyPromise
  }

  /**
   * 標記認證已就緒
   */
  setAuthReady(user: AuthUser | null = null): void {
    this.currentUser = user
    if (!this.isAuthReady) {
      this.isAuthReady = true
      this.authReadyResolve(user)
    }
  }

  /**
   * 初始化統一的認證狀態監聯器
   * 這是唯一的 onAuthStateChange 監聽點，其他地方應該使用 subscribe()
   */
  async initializeAuthListener(): Promise<AuthUser | null> {
    // 已經初始化完成，直接返回
    if (this._isInitialized) {
      return this.currentUser
    }

    // 正在初始化中，等待現有的 Promise 完成（防止並發調用）
    if (this._initializingPromise) {
      return this._initializingPromise
    }

    // 開始初始化，建立 Promise 供其他並發調用等待
    this._initializingPromise = this._doInitializeAuthListener()

    try {
      const result = await this._initializingPromise
      return result
    } finally {
      // 初始化完成後清除 Promise（無論成功或失敗）
      this._initializingPromise = null
    }
  }

  /**
   * 實際執行初始化邏輯（內部方法）
   */
  private async _doInitializeAuthListener(): Promise<AuthUser | null> {
    try {
      // OAuth 回調正常由 /auth/callback 頁面處理（AuthCallback.vue，PKCE flow）
      // 但若 Supabase 回 implicit flow（hash token），會 redirect 到根路徑，需要在這裡兜底
      const hasHashToken = window.location.hash.includes('access_token')
      if (hasHashToken) {
        console.log('[AuthService] 偵測到 hash token（implicit flow 兜底），手動建立 session')
        const hashParams = new URLSearchParams(window.location.hash.slice(1))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        if (accessToken && refreshToken) {
          try {
            const { data, error: setErr } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })
            if (!setErr && data?.session) {
              const user = await this.buildUserFromSession(data.session.user)
              this.currentUser = user
              this._notifyAuthStateChange('SIGNED_IN', user)
              this.setAuthReady(user)
              this._isInitialized = true
              this._setupAuthListener()
              if (user) this.startTokenRefreshTimer()
              // 清理 URL hash
              if (window.history?.replaceState) {
                window.history.replaceState({}, document.title, window.location.pathname)
              }
              console.log('[AuthService] Hash token 登入成功')
              return user
            }
          } catch (e) {
            console.warn('[AuthService] Hash token setSession 失敗:', (e as Error).message)
          }
        }
      }

      // PKCE code 兜底：若 redirectTo 快取為舊版（/），code 會回到根路徑
      const urlParams = new URLSearchParams(window.location.search)
      const pkceCode = urlParams.get('code')
      if (pkceCode) {
        console.log('[AuthService] 偵測到 PKCE code（兜底），手動 exchangeCodeForSession')
        try {
          const { data, error: codeErr } = await supabase.auth.exchangeCodeForSession(pkceCode)
          if (!codeErr && data?.session) {
            const user = await this.buildUserFromSession(data.session.user)
            this.currentUser = user
            this._notifyAuthStateChange('SIGNED_IN', user)
            this.setAuthReady(user)
            this._isInitialized = true
            this._setupAuthListener()
            if (user) this.startTokenRefreshTimer()
            // 清理 URL 的 code 參數
            if (window.history?.replaceState) {
              window.history.replaceState({}, document.title, window.location.pathname)
            }
            console.log('[AuthService] PKCE code 登入成功')
            return user
          } else if (codeErr) {
            console.warn('[AuthService] PKCE code exchange 失敗:', codeErr.message)
          }
        } catch (e) {
          console.warn('[AuthService] PKCE code exchange 異常:', (e as Error).message)
        }
      }

      // 獲取當前 session（帶超時保護 + localStorage fallback）
      let session: Session | null = null
      let sessionError: Error | null = null

      try {
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`初始化 getSession 超時 (${this.SESSION_TIMEOUT}ms)`)), this.SESSION_TIMEOUT)
        )
        const result = await Promise.race([sessionPromise, timeoutPromise])
        session = result.data?.session ?? null
        if (result.error) sessionError = result.error
      } catch (e) {
        console.warn('[AuthService] getSession 失敗或超時:', (e as Error).message)
      }

      // Fallback: getSession 失敗時嘗試從 localStorage 恢復
      if (!session) {
        try {
          const raw = localStorage.getItem('om-auth-token')
          if (raw) {
            const stored = JSON.parse(raw)
            const expiresAt = stored?.expires_at
            if (expiresAt && expiresAt * 1000 > Date.now()) {
              console.log('[AuthService] getSession 失敗，使用 localStorage fallback（token 未過期）')
              // 用 setSession 讓 Supabase 重新建立 session
              const { data, error: setErr } = await supabase.auth.setSession({
                access_token: stored.access_token,
                refresh_token: stored.refresh_token,
              })
              if (!setErr && data?.session) {
                session = data.session
              } else {
                console.warn('[AuthService] localStorage fallback setSession 失敗:', setErr?.message)
              }
            } else {
              console.log('[AuthService] localStorage token 已過期，需重新登入')
            }
          }
        } catch (fallbackErr) {
          console.warn('[AuthService] localStorage fallback 失敗:', fallbackErr)
        }
      }

      if (sessionError && !session) {
        console.error('[AuthService] 獲取 session 失敗:', sessionError)
        this._notifyAuthStateChange('INITIAL_SESSION', null)
        this.setAuthReady(null)
        return null
      }

      // 建立用戶物件
      const user = await this.buildUserFromSession(session?.user ?? null)
      this.currentUser = user

      // 通知初始狀態
      this._notifyAuthStateChange('INITIAL_SESSION', user)
      this.setAuthReady(user)

      // 設置 Supabase 監聽器
      this._isInitialized = true
      this._setupAuthListener()

      // 已登入的用戶啟動主動 Token 刷新定時器
      if (user) {
        this.startTokenRefreshTimer()
      }

      console.log('[AuthService] 認證監聯器初始化完成', user ? '(已登入)' : '(未登入)')
      return user
    } catch (error) {
      console.error('[AuthService] 初始化認證監聽器失敗:', error)
      this.setAuthReady(null)
      return null
    }
  }


  /**
   * 設置 Supabase 認證狀態監聯器（內部方法）
   */
  private _setupAuthListener(): void {
    // 避免重複設置
    if (this._authSubscription) {
      return
    }

    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthService] Auth 狀態變化:', event)

      if (event === 'SIGNED_IN' && session?.user) {
        const user = await this.buildUserFromSession(session.user)
        this.currentUser = user
        this._notifyAuthStateChange(event, user)

        // 啟動主動 Token 刷新定時器
        this.startTokenRefreshTimer()

        // 在確認登入成功後才清理 URL（避免過早清理導致 token 丟失）
        if (window.location.hash.includes('access_token') || window.location.search.includes('code=')) {
          this.cleanupUrlParams()
        }
      } else if (event === 'SIGNED_OUT') {
        this.currentUser = null
        this.clearCache()
        this.stopTokenRefreshTimer()
        this._notifyAuthStateChange(event, null)
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Token 刷新時清除 session 快取，讓下次 getSession 重新取得
        this.clearCache()
        this._notifyAuthStateChange(event, this.currentUser)
      } else if (event === 'USER_UPDATED' && session?.user) {
        const user = await this.buildUserFromSession(session.user)
        this.currentUser = user
        this._notifyAuthStateChange(event, user)
      }
    })

    this._authSubscription = data.subscription
  }

  /**
   * 從 Supabase session 建立 Firebase 相容的用戶物件
   */
  async buildUserFromSession(sessionUser: SupabaseUser | null): Promise<AuthUser | null> {
    if (!sessionUser) return null

    try {
      // 從 public.users 獲取完整的用戶資料
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .or(`id.eq.${sessionUser.id},supabase_user_id.eq.${sessionUser.id}`)
        .limit(1)
        .maybeSingle()

      if (userError) {
        console.warn('[AuthService] 獲取用戶資料失敗:', userError.message)
      }

      const meta = sessionUser.user_metadata || {}
      return {
        id: sessionUser.id,
        uid: sessionUser.id,
        email: sessionUser.email ?? null,
        displayName: userData?.display_name || meta.display_name || meta.full_name || meta.name || null,
        photoURL: userData?.photo_url || meta.photo_url || meta.avatar_url || meta.picture || null,
        emailVerified: !!sessionUser.email_confirmed_at,
        isSupabaseUser: true,
        supabaseUser: sessionUser,
        // 上次選擇的組織 ID（從 public.users 表取得）
        lastSelectedOrgId: userData?.last_selected_org_id || null
      }
    } catch (error) {
      console.error('[AuthService] buildUserFromSession 錯誤:', error)
      // 返回基本用戶資訊（無法取得 lastSelectedOrgId）
      const meta = sessionUser.user_metadata || {}
      return {
        id: sessionUser.id,
        uid: sessionUser.id,
        email: sessionUser.email ?? null,
        displayName: meta.display_name || meta.full_name || meta.name || null,
        photoURL: meta.photo_url || meta.avatar_url || meta.picture || null,
        emailVerified: !!sessionUser.email_confirmed_at,
        isSupabaseUser: true,
        supabaseUser: sessionUser,
        lastSelectedOrgId: null
      }
    }
  }

  /**
   * 訂閱認證狀態變化
   * @returns 取消訂閱的函數
   */
  onAuthStateChange(callback: AuthStateCallback): () => void {
    if (typeof callback !== 'function') {
      console.error('[AuthService] onAuthStateChange 需要一個函數作為參數')
      return () => {}
    }

    this._authStateCallbacks.add(callback)

    // 如果已經初始化，立即調用一次回調
    if (this._isInitialized) {
      try {
        callback('INITIAL_SESSION', this.currentUser)
      } catch (error) {
        console.error('[AuthService] 初始回調執行錯誤:', error)
      }
    }

    // 返回取消訂閱函數
    return () => {
      this._authStateCallbacks.delete(callback)
    }
  }

  /**
   * 通知所有訂閱者認證狀態變化
   */
  private _notifyAuthStateChange(event: AuthEvent, user: AuthUser | null): void {
    this._authStateCallbacks.forEach(callback => {
      try {
        callback(event, user)
      } catch (error) {
        console.error('[AuthService] 認證回調執行錯誤:', error)
      }
    })
  }

  /**
   * 銷毀認證監聽器
   */
  destroy(): void {
    if (this._authSubscription) {
      this._authSubscription.unsubscribe()
      this._authSubscription = null
    }
    this._authStateCallbacks.clear()
    this._isInitialized = false
  }


  // ============ Session 快取（delegation → sessionCacheService）============

  async getSession(forceRefresh = false): Promise<Session | null> {
    return sessionCacheService.getSession(forceRefresh)
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUser
  }

  setCurrentUser(user: AuthUser | null): void {
    this.currentUser = user
  }

  async refreshToken(): Promise<Session | null> {
    return sessionCacheService.refreshToken()
  }

  startTokenRefreshTimer(): void {
    sessionCacheService.startTokenRefreshTimer()
  }

  stopTokenRefreshTimer(): void {
    sessionCacheService.stopTokenRefreshTimer()
  }

  clearCache(): void {
    sessionCacheService.clearCache()
  }

  isCacheValid(): boolean {
    return sessionCacheService.isCacheValid()
  }

  async signOut(): Promise<void> {
    try {
      this.stopTokenRefreshTimer()
      await supabase.auth.signOut()
      this.clearCache()
      this.clearAuthState()
      this.currentUser = null
      console.log('[AuthService] 已登出')
    } catch (error) {
      console.error('[AuthService] 登出失敗:', error)
    }
  }

  getStats(): AuthStats {
    return {
      isCached: this.isCacheValid(),
      hasSession: sessionCacheService.isCacheValid(),
      cacheExpireIn: -1,  // delegation to sessionCacheService, exact value not available
      isAuthReady: this.isAuthReady,
      hasUser: this.currentUser !== null
    }
  }



  /**
   * 檢查 session 並同步用戶資料（App.vue 的 checkSupabaseAuth 呼叫）
   */
  async handleOAuthCallback(): Promise<AuthUser | null> {
    try {
      // 檢查 URL 中是否有 OAuth 錯誤參數
      const urlParams = new URLSearchParams(window.location.search)
      const errorParam = urlParams.get('error')

      if (errorParam) {
        const errorDescription = urlParams.get('error_description')
        console.error('[AuthService] OAuth 錯誤:', errorParam, errorDescription)

        if (!(errorParam === 'server_error' && errorDescription?.includes('Database error'))) {
          const error: OAuthError = new Error(errorDescription || errorParam)
          error.code = errorParam
          throw error
        }
        console.warn('[AuthService] 資料庫觸發器錯誤，嘗試繼續取得 session')
      }

      // 取得 session
      const session = await this.getSession(true)
      if (!session?.user) return null

      // 同步用戶資料到 DB（delegation → userProfileService）
      try {
        await userProfileService.createOrUpdateUserProfile(session.user)
      } catch (syncErr) {
        console.warn('[AuthService] 用戶資料同步失敗:', syncErr)
      }

      // 建立認證用戶物件
      const authUser = convertSupabaseUserToAuthUser(session.user)
      if (!authUser) return null

      this.setCurrentUser(authUser)

      // 清理 URL 參數（如果有殘留的 OAuth 參數）
      if (window.location.hash.includes('access_token') || window.location.search.includes('code=')) {
        this.cleanupUrlParams()
      }

      return authUser
    } catch (error) {
      const err = error as Error
      console.error('[AuthService] handleOAuthCallback 錯誤:', err.message)
      throw error
    }
  }

  /**
   * 清理 URL 參數
   */
  cleanupUrlParams(): void {
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }


  // ============ 用戶資料管理（delegation → userProfileService）============

  clearUserCache(): void {
    userProfileService.clearUserCache()
  }

  async createOrUpdateUserProfile(user: SupabaseUser | AuthUser): Promise<string> {
    return userProfileService.createOrUpdateUserProfile(user)
  }

  async findUserByField(fieldName: string, fieldValue: string): Promise<UserProfile | null> {
    return userProfileService.findUserByField(fieldName, fieldValue)
  }

  async getUserProfile(user: string | AuthUser): Promise<UserProfile> {
    return userProfileService.getUserProfile(user)
  }

  async updateUserProfile(userProfile: UserProfile): Promise<void> {
    return userProfileService.updateUserProfile(userProfile)
  }

  // ============ OAuth 登入（delegation → oauthProviders）============

  async signUp(email: string, password: string): Promise<AuthUser> {
    return _signUp(email, password)
  }

  async signInWithPassword(email: string, password: string): Promise<AuthUser> {
    return _signInWithPassword(email, password)
  }

  async signInWithGoogle(): Promise<AuthUser | null> {
    return _signInWithGoogle()
  }

  async signInWithLine(): Promise<AuthUser | null> {
    return _signInWithLine()
  }

  async signInWithFacebook(): Promise<AuthUser | null> {
    return _signInWithFacebook()
  }

  async signInWithProvider(providerName: ProviderName): Promise<AuthUser | null> {
    return _signInWithProvider(providerName)
  }

  async signInWithRedirect(providerName: ProviderName): Promise<AuthUser | null> {
    return _signInWithRedirect(providerName)
  }

  // ============ 用戶偏好（delegation → userPreferencesService）============

  async saveLastSelectedOrganization(userId: string, orgId: string): Promise<void> {
    return _saveLastSelectedOrganization(userId, orgId)
  }

  async getLastSelectedOrganization(userId: string): Promise<string | null> {
    return _getLastSelectedOrganization(userId)
  }

  async saveFilterPreferences(userId: string, filter: string, city: string): Promise<void> {
    return _saveFilterPreferences(userId, filter, city)
  }

  async getFilterPreferences(userId: string): Promise<{ filter: string; city: string } | null> {
    return _getFilterPreferences(userId)
  }

  // ============ 其他 ============

  clearAuthState(): void {
    try {
      localStorage.removeItem('auth_redirect_state')
      localStorage.removeItem('auth_last_login_user')
      console.log('[AuthService] 已清理 auth 臨時狀態')
    } catch (e) {
      console.error('[AuthService] 清除 Auth 狀態失敗:', e)
    }
  }
}


// 導出單例
export const authService = new AuthService()

/**
 * 檢查錯誤是否是 token 過期錯誤
 */
export function isTokenExpiredError(error: unknown): boolean {
  if (!error) return false
  const errorMessage = error instanceof Error ? error.message : String(error)
  return errorMessage.includes('token is expired') ||
         errorMessage.includes('invalid JWT') ||
         errorMessage.includes('unable to parse or verify signature') ||
         errorMessage.includes('token has invalid claims')
}

/**
 * 處理 token 過期錯誤，嘗試刷新 session
 */
export async function handleTokenExpiredError(error: unknown): Promise<Session | null> {
  if (!isTokenExpiredError(error)) {
    return null
  }

  console.warn('[AuthService] 檢測到 token 過期，嘗試刷新...')

  try {
    const session = await authService.refreshToken()
    if (session) {
      return session
    } else {
      console.error('[AuthService] Session 刷新失敗，需要重新登入')
      await authService.signOut()
      return null
    }
  } catch (refreshErr) {
    console.error('[AuthService] Session 刷新失敗:', refreshErr)
    await authService.signOut()
    return null
  }
}

/**
 * 包裝 Supabase 請求，自動處理 token 過期錯誤
 */
export async function withTokenRefresh<T>(
  requestFn: () => Promise<T>,
  maxRetries = 1
): Promise<T> {
  let lastError: unknown = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await requestFn()

      // 檢查結果中是否有錯誤
      const resultWithError = result as { error?: unknown }
      if (resultWithError?.error && isTokenExpiredError(resultWithError.error)) {
        if (attempt < maxRetries) {
          console.warn(`[AuthService] 請求失敗（token 過期），重試 (${attempt + 1}/${maxRetries})...`)
          const refreshedSession = await handleTokenExpiredError(resultWithError.error)
          if (!refreshedSession) {
            throw new Error('Session 刷新失敗，無法繼續請求')
          }
          continue
        } else {
          throw resultWithError.error
        }
      }

      return result
    } catch (error) {
      lastError = error

      if (isTokenExpiredError(error)) {
        if (attempt < maxRetries) {
          console.warn(`[AuthService] 請求失敗（token 過期），重試 (${attempt + 1}/${maxRetries})...`)
          const refreshedSession = await handleTokenExpiredError(error)
          if (!refreshedSession) {
            throw new Error('Session 刷新失敗，無法繼續請求')
          }
          continue
        } else {
          throw error
        }
      } else {
        throw error
      }
    }
  }

  throw lastError
}

// 向後兼容：導出 sessionManager 別名
export const sessionManager = authService

// 向後兼容：導出 getSupabaseSession 函數
export async function getSupabaseSession(forceRefresh = false): Promise<Session | null> {
  return authService.getSession(forceRefresh)
}

export default authService
